import * as React from "react";
import { useMemo } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  CardHeader,
  CardContent,
  Link,
  Typography,
  TextField,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";

import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Urls } from "../domain/urls";
import { REDIRECT_URI_PARAM } from "./constants";
import {
  validateEmail,
  validatePassword,
  validateUserName,
} from "../utils/validation";
import { signUp } from "./auth";
import { AuthTheme } from "./AuthLayout";
import { ActivationFailReasons } from "./types";
import { GithubIcon } from "../icons/GithubIcon";
import { GoogleIcon } from "../icons/GoogleIcon";
import { Page } from "../components/layout/page";
import {
  StyledAuthForm,
  Group,
  HalfDivider,
  SectionCard,
  ThirdPartyButton,
  Header,
} from "./components";
import { analytics } from "../analytics";
import { CardStack, TesimonialCard, CardTitle, CardText } from "./CardStack";
import { LoadingButton } from "@mui/lab";

type Inputs = {
  fullName: string;
  preferredName: string;
  username: string;
  password1: string;
  password2: string;
  email: string;
};

export const SignUpForm = (): React.ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSuccess, setSuccess] = React.useState<boolean>(false);
  const [emailVerified, setEmailVerified] = React.useState<boolean>(false);
  const [nonFieldError, setNonFieldError] = React.useState<string>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [tooltipIsOpen, setTooltipIsOpen] = React.useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    setError,
    formState: { errors, isValid },
  } = useForm<Inputs>({ mode: "onChange" });

  const onSubmit = async ({
    fullName,
    preferredName,
    username,
    email,
    password1,
    password2,
  }: Inputs) => {
    setIsLoading(true);
    const result = await signUp({
      fullName,
      preferredName,
      username,
      email,
      password1,
      password2,
    });
    if (result.type === "success") {
      setSuccess(true);
    }
    if (result.type === "alreadyVerified") {
      setEmailVerified(true);
    }
    if (result.type === "error") {
      const responseErrs = result?.errors;
      if (responseErrs?.full_name) {
        setError("fullName", {
          type: "manual",
          message: responseErrs.full_name.join(","),
        });
      }
      if (responseErrs?.preferred_name) {
        setError("preferredName", {
          type: "manual",
          message: responseErrs.preferred_name.join(","),
        });
      }
      if (responseErrs?.username) {
        setError("username", {
          type: "manual",
          message: responseErrs.username.join(","),
        });
      }
      if (responseErrs?.email) {
        setError("email", {
          type: "manual",
          message: responseErrs.email.join(","),
        });
      }
      if (responseErrs?.password1) {
        setError("password1", {
          type: "manual",
          message: responseErrs.password1.join(","),
        });
      }
      if (responseErrs?.password2) {
        setError("password2", {
          type: "manual",
          message: responseErrs.password2.join(","),
        });
      }
      if (responseErrs?.non_field_errors) {
        setNonFieldError(responseErrs.non_field_errors[0]);
      } else if (responseErrs?.unknownError) {
        setNonFieldError(responseErrs.unknownError);
      }
    }

    setIsLoading(false);
  };

  if (emailVerified === true) {
    navigate({ pathname: `${Urls.Root}` });
  }

  if (isSuccess === true) {
    // Preserve redirect_uri parameter when navigating to verify email sent page
    const redirectUri = searchParams.get(REDIRECT_URI_PARAM);
    const verifyEmailSentUrl = redirectUri
      ? {
          pathname: `/${Urls.VerifyEmailSent}`,
          search: `?${REDIRECT_URI_PARAM}=${encodeURIComponent(redirectUri)}`,
        }
      : { pathname: `/${Urls.VerifyEmailSent}` };
    navigate(verifyEmailSentUrl);
  }

  const breadCrumbs = useMemo(() => [], []);
  return (
    <Page title="Sign Up" breadCrumbs={breadCrumbs} noPadding>
      <Helmet>
        <script
          defer
          data-domain="coiled.io,cloud.coiled.io"
          src="https://plausible.io/js/script.js"
        />
      </Helmet>
      <AuthTheme>
        <Header />
        <Box
          sx={{
            minHeight: "100vh",
            backgroundColor: "#101716",
            width: "100%",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              width: "50%",
              background:
                "radial-gradient(495.78% 97.39% at 50% 103.93%, #2F514C 0%, #041615 15.52%, #040B09 48.01%)",
              display: { xs: "none", md: "block" },
            }}
          />
          <Grid
            container
            sx={{
              maxWidth: "1450px",
              margin: "0 auto",
              minHeight: "100vh",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Grid
              xs={12}
              md={6}
              display="flex"
              flexDirection="column"
              alignItems="center"
              minHeight="100vh"
              sx={{
                paddingTop: "64px",
              }}
            >
              <SectionCard>
                <CardHeader
                  title={
                    <>
                      Create your
                      <br />
                      Coiled account
                    </>
                  }
                  titleTypographyProps={{
                    variant: "h4",
                  }}
                />
                <CardContent sx={{ width: "100%" }}>
                  <StyledAuthForm onSubmit={handleSubmit(onSubmit)}>
                    {nonFieldError && (
                      <Typography align="center" color="#FF9172">
                        {nonFieldError}
                      </Typography>
                    )}
                    <Group>
                      <ThirdPartyButton
                        icon={<GoogleIcon />}
                        href={`/${Urls.GoogleInitLogin}`}
                        onClick={() =>
                          analytics.track("signup-clicked", {
                            provider: "google",
                          })
                        }
                      >
                        Continue with Google
                      </ThirdPartyButton>
                      <ThirdPartyButton
                        icon={<GithubIcon />}
                        href={`/${Urls.GithubInitLogin}`}
                        onClick={() =>
                          analytics.track("signup-clicked", {
                            provider: "github",
                          })
                        }
                      >
                        Continue with GitHub
                      </ThirdPartyButton>
                    </Group>

                    <HalfDivider>OR, FIND YOUR TEAM</HalfDivider>

                    <Group>
                      <Box>
                        <TextField
                          {...register("fullName", {
                            required: "Full name is required",
                            validate: (value) =>
                              value.length <= 254 ||
                              "Full name is limited to 254 or less characters",
                          })}
                          error={!!errors.fullName}
                          helperText={
                            errors.fullName ? errors.fullName.message : ""
                          }
                          fullWidth
                          variant="outlined"
                          label="Full name"
                          id="fullName"
                        />
                      </Box>

                      <Box>
                        <TextField
                          {...register("preferredName", {
                            validate: (value) =>
                              value.length <= 150 ||
                              "Preferred name is limited to 150 or less characters",
                          })}
                          error={!!errors.preferredName}
                          helperText={
                            errors.preferredName
                              ? errors.preferredName.message
                              : ""
                          }
                          fullWidth
                          variant="outlined"
                          label="Preferred name (optional)"
                          required={false}
                          id="preferredName"
                        />
                      </Box>

                      <Box>
                        <TextField
                          {...register("username", {
                            required: "Username is required",
                            validate: (value) =>
                              validateUserName(value) === true ||
                              ActivationFailReasons.UsernameRequirements,
                          })}
                          error={!!errors.username}
                          helperText={
                            errors.username ? errors.username.message : ""
                          }
                          fullWidth
                          variant="outlined"
                          label="Username"
                          id="username"
                          // Hack to prevent Safari autofill from showing a weird yellow background color
                          sx={{
                            "& input:-webkit-autofill": {
                              WebkitBoxShadow: "0 0 0 100px #121212 inset",
                              WebkitTextFillColor: "#fff",
                            },
                          }}
                        />
                      </Box>

                      <Box>
                        <TextField
                          {...register("email", {
                            required: "Work email is required",
                            validate: (value) =>
                              validateEmail(value) === true || "Invalid email",
                          })}
                          error={!!errors.email}
                          helperText={errors.email ? errors.email.message : ""}
                          fullWidth
                          variant="outlined"
                          label="Work email"
                          id="email"
                        />
                      </Box>

                      <Box>
                        <TextField
                          {...register("password1", {
                            required: "Password is required",
                            validate: (value) =>
                              validatePassword(value) === true ||
                              ActivationFailReasons.PasswordRequirements,
                          })}
                          error={!!errors.password1}
                          helperText={
                            errors.password1 ? errors.password1.message : ""
                          }
                          fullWidth
                          variant="outlined"
                          label="Password"
                          type="password"
                          id="password1"
                          // Hack to prevent Safari autofill from showing a weird yellow background color
                          sx={{
                            "& input:-webkit-autofill": {
                              WebkitBoxShadow: "0 0 0 100px #121212 inset",
                              WebkitTextFillColor: "#fff",
                            },
                          }}
                        />
                      </Box>

                      <Box>
                        <TextField
                          {...register("password2", {
                            required: "Matching password is required",
                            validate: (value) =>
                              value === getValues("password1") ||
                              "Passwords must match",
                          })}
                          error={!!errors.password2}
                          helperText={
                            errors.password2 ? errors.password2.message : ""
                          }
                          fullWidth
                          variant="outlined"
                          label="Confirm password"
                          type="password"
                          id="password2"
                          // Hack to prevent Safari autofill from showing a weird yellow background color
                          sx={{
                            "& input:-webkit-autofill": {
                              WebkitBoxShadow: "0 0 0 100px #121212 inset",
                              WebkitTextFillColor: "#fff",
                            },
                          }}
                        />
                      </Box>

                      <Tooltip
                        title="To submit, fill out all required fields"
                        placement="bottom"
                        open={tooltipIsOpen}
                        onOpen={() => !isValid && setTooltipIsOpen(true)}
                        onClose={() => setTooltipIsOpen(false)}
                      >
                        <span>
                          <LoadingButton
                            fullWidth
                            type="submit"
                            loading={isLoading}
                            disabled={!isValid}
                            variant={!isValid ? "outlined" : "contained"}
                            color={!isValid ? "secondary" : "primary"}
                          >
                            Create account
                          </LoadingButton>
                        </span>
                      </Tooltip>
                    </Group>
                    <Group>
                      <Typography variant="body2" color="text.secondary">
                        Want the white glove treatment?{" "}
                        <Link href="https://coiled.io/request-demo">
                          Book a demo
                        </Link>
                      </Typography>
                    </Group>
                  </StyledAuthForm>
                </CardContent>
              </SectionCard>
              <Box textAlign="center" mb={2}>
                <Typography variant="body2" color="text.secondary">
                  By using Coiled, you are agreeing to our
                  <br />
                  <Link href="https://coiled.io/terms-and-conditions">
                    Terms of Service
                  </Link>
                </Typography>
              </Box>
            </Grid>
            <Grid
              xs={12}
              md={6}
              display={{ xs: "none", md: "flex" }}
              alignItems="center"
              justifyContent="center"
            >
              <Box sx={{ width: "100%", maxWidth: "725px", margin: "0 auto" }}>
                <CardStack>
                  <TesimonialCard>
                    <CardText>
                      “The speed is nice, sure, but the real benefit is taking a
                      multi-day effort and finishing it in an afternoon.{" "}
                      <span style={{ color: "white" }}>
                        Coiled changed the character of our work.
                      </span>
                      “
                    </CardText>
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap="16px"
                      alignItems="center"
                    >
                      <CardTitle>Matt Plough, Software Engineer</CardTitle>
                      <img
                        src="/static/customer-logos/kobold.png"
                        alt="KoBold Metals"
                        style={{ width: "140px" }}
                      />
                    </Box>
                  </TesimonialCard>
                  <TesimonialCard>
                    <CardText>
                      “Quite literally{" "}
                      <span style={{ color: "white" }}>
                        burst to the cloud from your laptop
                      </span>
                      , everything I&apos;ve been dreaming of since grad
                      school.“
                    </CardText>
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap="16px"
                      alignItems="center"
                    >
                      <CardTitle>Eric Ma, Data Scientist</CardTitle>
                      <img
                        src="/static/customer-logos/moderna.png"
                        alt="Moderna"
                        style={{ width: "140px" }}
                      />
                    </Box>
                  </TesimonialCard>
                  <TesimonialCard>
                    <CardText>
                      “
                      <span style={{ color: "white" }}>
                        Coiled support is amazing
                      </span>
                      . I&apos;ll run into an issue and before I have a chance
                      to mention it I have an email in my inbox. You don&apos;t
                      get this kind of support with large companies.“
                    </CardText>
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap="16px"
                      alignItems="center"
                    >
                      <CardTitle>Katya Potapov, Software Engineer</CardTitle>
                      <img
                        src="/static/customer-logos/floodbase.png"
                        alt="Floodbase"
                        style={{ width: "140px" }}
                      />
                    </Box>
                  </TesimonialCard>
                </CardStack>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </AuthTheme>
    </Page>
  );
};
